import playdateService from "@/api/services/playdateService";
import { getCookie } from "@/utils/cookies";
import {
  EXISTING_POPUPS,
  FEATURE_AGE_VERIFICATION,
  POPUP_AGE_VERIFICATION_SUCCESS,
  POPUP_BUY_SUBSCRIPTION,
  POPUP_CAMERA_FEEDBACK,
  POPUP_CHANGE_AVATAR,
  POPUP_COINSCHARGE,
  POPUP_COINSCHARGE_SUCCESS,
  POPUP_COOKIES,
  POPUP_DISCOUNT,
  POPUP_DOUBLE_OPT_IN_FINISH,
  POPUP_FEATURE_FEEDBACK,
  POPUP_FEATURE_INTRODUCE,
  POPUP_FEEDBACK,
  POPUP_FEEDBACK_REMOVAL,
  POPUP_FEEDBACK_SUCCESS,
  POPUP_LAW_LIMITATION,
  POPUP_MODEL_TIP,
  POPUP_MODEL_TIP_OFFER,
  POPUP_MODEL_TIP_SUCCESS,
  POPUP_ORGANIC,
  POPUP_PLAYDATE_FEEDBACK,
  POPUP_PLAYDATE_FORBIDDANCE,
  POPUP_PLAYDATE_REQUEST_SUCCESS,
  POPUP_TRANSACTIONAL,
  POPUP_MANUAL_CANCELLATION,
  SUCCESS_BUY_COOKIE,
} from "@/utils/variables";
import { getTld } from "@/utils/urlHelper.ts";
import getFlash from "@/utils/getFlash";
import messageService from "@/api/services/messageService";
import { getApprovedCookies, getPopup, removePopup, setPopup, updatePopup } from "@/utils/storage";
import popupConfig from "@/configs/popup-config";
import router from "@/router/router.ts";
import { useUserStore } from "@/store/user";
import { useFeatureStore } from "@/store/feature";
import { useVisitStore } from "@/store/visit";
import { defineStore } from "pinia";

interface PopupData {
  show: boolean;
  data: Record<string, any>;
}

interface PopupState {
  popups: Record<string, PopupData>;
}

export const usePopupStore = defineStore("popup", {
  state: (): PopupState => ({
    popups: {},
  }),

  getters: {
    canShow:
      (state: PopupState) =>
      (id: string, byDefault: boolean = false): boolean => {
        if (popupConfig[id].requiresAuth && !useUserStore().isLoggedIn) {
          return false;
        }

        if (state.popups[id] !== undefined) {
          return state.popups[id].show ?? byDefault;
        }

        const popup = getPopup(id);
        return popup?.show ?? byDefault;
      },
    getPopupData:
      (state: PopupState) =>
      (id: string): Record<string, any> => {
        if (state.popups[id] !== undefined) {
          return state.popups[id].data ?? null;
        }

        const popup = getPopup(id);
        return popup?.data ?? null;
      },
    canShowSubscriptionPopup:
      () =>
      (path: string): boolean => {
        const success = getCookie("success_buy") || false;

        if (success) {
          return false;
        }

        if (useUserStore().isPremium) {
          return false;
        }

        if (useFeatureStore().hasAgeVerificationFeature && (path === "/agb" || path === "/contact-us")) {
          return false;
        }

        const canShow = usePopupStore().canShow(POPUP_BUY_SUBSCRIPTION);

        if (useFeatureStore().showDiscountAsPage && canShow) {
          router.push("/paywall");
          return false;
        }

        return useFeatureStore().hasAgeVerificationFeature || canShow;
      },
    canShowFeedbackPopup(): boolean {
      return this.canShow(POPUP_FEEDBACK);
    },
    canShowFeedbackSuccessPopup(): boolean {
      return this.canShow(POPUP_FEEDBACK_SUCCESS);
    },
    canShowFeedbackRemovalPopup(): boolean {
      return this.canShow(POPUP_FEEDBACK_REMOVAL);
    },
    canShowLawRestrictionPopup() {
      if (useUserStore().isLoggedIn) {
        return false;
      }

      if (useFeatureStore().hasAgeVerificationFeature) {
        return false;
      }

      const restrictedRegions = [
        "texas",
        "alabama",
        "florida",
        "indiana",
        "kansas",
        "louisiana",
        "tennessee",
        "oklahoma",
        "south dakota",
        "south carolina",
        "kentucky",
        "georgia",
        "missouri",
      ];

      const visit = useVisitStore().getVisit;
      return restrictedRegions.indexOf(visit?.current_region?.toLowerCase() ?? "") > -1;
    },
    canShowLawLimitationPopup(): boolean {
      return this.canShow(POPUP_LAW_LIMITATION);
    },
    canShowAgeVerificationPopup: () => (path: string) => {
      const featureStore = useFeatureStore();

      if (featureStore.hasAgeVerificationFeature && (path === "/agb" || path === "/contact-us")) {
        return false;
      }

      const userStore = useUserStore();

      if (!userStore.isPremium && !getCookie(SUCCESS_BUY_COOKIE)) {
        return false;
      }

      if (userStore.getProfile?.age_verified !== false) {
        return false;
      }

      const feature = featureStore.getFeature(FEATURE_AGE_VERIFICATION);
      return feature?.settings?.can_show_av_popup || false;
    },
    canShowCoinsChargePopup(): boolean {
      const success = getCookie("success_coinscharge") || false;

      if (success) {
        return false;
      }

      return this.canShow(POPUP_COINSCHARGE);
    },
    isCoinsChargePopupBlank(): boolean {
      return this.getPopupData(POPUP_COINSCHARGE)?.blank ?? false;
    },
    canShowPlaydateFeedbackPopup(): boolean {
      return this.canShow(POPUP_PLAYDATE_FEEDBACK);
    },
    getPlaydateFeedbackPopup(): Record<string, any> {
      return this.getPopupData(POPUP_PLAYDATE_FEEDBACK) ?? null;
    },
    canShowCookiesPopup(): boolean {
      if (!useVisitStore().isEUCountry || getTld() === "io") {
        return false;
      }

      const show = this.canShow(POPUP_COOKIES, true);

      if (!show) {
        return false;
      }

      return !getApprovedCookies();
    },
    canShowSuccessCoinschargePopup(): boolean {
      const show = this.canShow(POPUP_COINSCHARGE_SUCCESS, true);
      return !show ? false : getFlash("success_coinscharge") !== null;
    },
    canShowSuccessAgeVerificationPopup(): boolean {
      const show = this.canShow(POPUP_AGE_VERIFICATION_SUCCESS, true);
      return !show ? false : getFlash("success_age_verification") !== null;
    },
    canShowPlaydateForbiddancePopup(): boolean {
      return this.canShow(POPUP_PLAYDATE_FORBIDDANCE);
    },
    getPlaydateForbiddancePopupData(): any {
      return this.getPopupData(POPUP_PLAYDATE_FORBIDDANCE)?.data ?? null;
    },
    getPlaydateForbiddancePopupAskCamera(): number | null {
      return this.getPopupData(POPUP_PLAYDATE_FORBIDDANCE)?.askCamera ?? null;
    },
    canShowPlaydateRequestSuccessPopup(): boolean {
      const show = this.canShow(POPUP_PLAYDATE_REQUEST_SUCCESS, true);
      return !show ? false : getFlash("success_playdate_request");
    },
    canShowTransactionalPopup(): boolean {
      setPopup(POPUP_TRANSACTIONAL, false);
      return this.canShow(POPUP_TRANSACTIONAL);
    },
    getTransactionalPopupMessage(): Record<string, any> {
      return this.getPopupData(POPUP_TRANSACTIONAL) ?? null;
    },
    canShowFreechatPopup(): boolean {
      return useFeatureStore().hasFreechatFeature;
    },
    canShowDoubleOptInPopup(): boolean {
      const userStore = useUserStore();

      if (userStore.isLoggedIn) {
        if (getCookie(SUCCESS_BUY_COOKIE)) {
          return false;
        }

        return useFeatureStore().hasDoubleOptInFeature;
      }

      return false;
    },
    canShowDoubleOptInFinishPopup(): boolean {
      return this.canShow(POPUP_DOUBLE_OPT_IN_FINISH);
    },
    canShowOrganicPopup(): boolean {
      const show = this.canShow(POPUP_ORGANIC, true);

      if (!show) {
        return false;
      }

      return useFeatureStore().hasOrganicPopupFeature;
    },
    canShowChangeAvatarPopup(): boolean {
      return this.canShow(POPUP_CHANGE_AVATAR);
    },
    canShowFeatureFeedbackPopup(): boolean {
      const show = this.canShow(POPUP_FEATURE_FEEDBACK, true);

      if (!show) {
        return false;
      }

      return useFeatureStore().showFeedbackPopup;
    },
    canShowFeatureIntroducePopup(): boolean {
      const show = this.canShow(POPUP_FEATURE_INTRODUCE, true);

      if (!show) {
        return false;
      }

      return useFeatureStore().showIntroducePopup;
    },
    canShowModelTipPopup(): boolean {
      return this.canShow(POPUP_MODEL_TIP);
    },
    canShowModelTipOfferPopup(): boolean {
      return this.canShow(POPUP_MODEL_TIP_OFFER);
    },
    canShowModelTipSuccessPopup(): boolean {
      const show = this.canShow(POPUP_MODEL_TIP_SUCCESS, true);
      return !show ? false : getFlash("success_model_tip");
    },
    canShowDiscountPopup(): boolean {
      const show = this.canShow(POPUP_DISCOUNT, true);

      if (!show) {
        return false;
      }

      const profile = useUserStore().getProfile;

      const feature = profile?.features?.summer_discount_26;
      return (feature?.is_active && !feature?.version?.includes("subscribers")) || false;
    },
    canShowCameraFeedbackPopup(): boolean {
      return this.canShow(POPUP_CAMERA_FEEDBACK);
    },
    canShowManualCancellationPopup(): boolean {
      return this.canShow(POPUP_MANUAL_CANCELLATION);
    },
  },

  actions: {
    setShow(id: string, show: boolean, data: any = null) {
      const popups: Record<string, PopupData> = {};

      popups[id] = {
        show: show,
        data: data,
      };

      this.popups = {
        ...this.popups,
        ...popups,
      };
    },
    show(id: string, data: any = null) {
      this.setShow(id, true, data);
      setPopup(id, true, data);
    },
    hide(id: string, data: any = null, remove: boolean = false) {
      this.setShow(id, false, data);

      if (remove) {
        removePopup(id);
      } else {
        setPopup(id, false, data);
      }
    },
    update(id: string, data: any = null) {
      const popups: Record<string, PopupData> = {};
      const popupData = this.popups[id]?.data || {};
      const newData = { ...popupData, ...data };

      popups[id] = {
        show: true,
        data: newData,
      };

      this.popups = {
        ...this.popups,
        ...popups,
      };

      updatePopup(id, data);
    },
    closeAll() {
      EXISTING_POPUPS.forEach((id) => {
        this.hide(id, null, true);
      });
    },
    async checkPlaydateFeedbackPopup() {
      this.hide(POPUP_PLAYDATE_FEEDBACK, null);

      const response = await playdateService.getFeedback();

      if (response !== null) {
        this.show(POPUP_PLAYDATE_FEEDBACK, response);
      }
    },

    async checkTransactionalMessagePopup() {
      const popupMessage = await messageService.getTransactionalMessagePopup();

      if (popupMessage !== null) {
        this.show(POPUP_TRANSACTIONAL, popupMessage);
      }
    },
  },
});
